import { useEffect, useMemo, useState } from 'react';
import DashboardChart from './DashboardChart';
import { ApexOptions } from 'apexcharts';
import { useDashboardContext } from './Context';
import { useAuthContext } from '../../contexts/AuthContext';
import { DashboardService } from './service';
import KpiCard from './KpiCard';
import { ChartPie, DollarSign, Receipt, RefreshCw } from 'lucide-react';

const service = new DashboardService();

const formatCurrency = (value: number) =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

export default function ExecutiveDashboard() {
    const { startAt, endAt, atendenteId } = useDashboardContext();
    const { user } = useAuthContext();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<any>(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const params: any = { startAt, endAt };
                if (user?.admin || user?.root) {
                    if (atendenteId) params.attendantId = atendenteId;
                }
                const response = await service.getExecutivo(params);
                setData(response.data);
            } catch (error) {
                console.error('Erro ao carregar dashboard executivo', error);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [startAt, endAt, atendenteId, user?.admin, user?.root]);

    const trend = useMemo(() => {
        const series = [
            {
                name: 'Faturamento de serviços',
                data: data?.trend?.revenue || [],
            },
            {
                name: 'Atendimentos',
                data: data?.trend?.appointments || [],
            },
        ];
        const options: ApexOptions = {
            chart: { type: 'line', toolbar: { show: false }, sparkline: { enabled: false } },
            stroke: { curve: 'smooth', width: 2.5 },
            xaxis: { categories: data?.trend?.dates || [] },
            yaxis: { labels: { formatter: (val: number) => Math.round(val).toString() } },
            colors: ['#007aff', '#34c759'],
            tooltip: { y: { formatter: (val: number) => val.toFixed(0) } },
            grid: { borderColor: 'rgba(0,0,0,0.05)' },
        };
        return {
            series,
            options,
        };
    }, [data]);

    if (!data) {
        return (
            <div className="card sf-dashboard-panel">
                <div className="card-body">{loading ? 'Carregando...' : 'Sem dados'}</div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <KpiCard
                    title="Faturamento de serviços"
                    value={formatCurrency(data.summary.revenue)}
                    delta={data.summary.revenueDelta}
                    tone="success"
                    icon={DollarSign}
                />
                <KpiCard
                    title="Ticket médio"
                    value={formatCurrency(data.summary.ticket)}
                    delta={data.summary.ticketDelta}
                    tone="primary"
                    icon={Receipt}
                />
                <KpiCard
                    title="Ocupação"
                    value={formatPercent(data.summary.occupancyPercent)}
                    delta={data.summary.occupancyDelta}
                    tone="info"
                    icon={ChartPie}
                />
                <KpiCard
                    title="Rebooking"
                    value={formatPercent(data.summary.rebookingRate)}
                    delta={data.summary.rebookingDelta}
                    tone="secondary"
                    icon={RefreshCw}
                />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <div className="card sf-dashboard-panel xl:col-span-2">
                    <div className="card-body">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold">Tendência do período</h3>
                            <span className="text-xs text-base-content/60">
                                {data.range?.startAt?.slice(0, 10)} - {data.range?.endAt?.slice(0, 10)}
                            </span>
                        </div>
                        <DashboardChart
                            options={trend.options}
                            series={trend.series}
                            type="line"
                            height={260}
                        />
                    </div>
                </div>

                <div className="card sf-dashboard-panel">
                    <div className="card-body">
                        <h3 className="text-lg font-semibold">Top serviços</h3>
                        <div className="mt-3 space-y-3">
                            <div>
                                <div className="text-xs uppercase text-base-content/60">Por receita</div>
                                <ul className="mt-2 space-y-2">
                                    {(data.topServices?.byRevenue || []).map((item: any) => (
                                        <li key={item.servicoId} className="flex items-center justify-between">
                                            <span className="text-sm">{item.name}</span>
                                            <span className="text-sm font-medium">{formatCurrency(item.totalRevenue)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <div className="text-xs uppercase text-base-content/60">Por volume</div>
                                <ul className="mt-2 space-y-2">
                                    {(data.topServices?.byVolume || []).map((item: any) => (
                                        <li key={item.servicoId} className="flex items-center justify-between">
                                            <span className="text-sm">{item.name}</span>
                                            <span className="text-sm font-medium">{item.count} atend.</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
